import { useEffect, useEffectEvent } from 'react';

import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { selectUserId } from '@/redux/user/selectors';
import { ServerToClientEvents, Socket } from '@/shared/types/rtm';

import { RtmActions, useRtm } from './useRtm';

/**
 * Declarative socket event handling.
 *
 * A resource's real-time behaviour is a table of bindings — which server event, and what to do
 * with its payload — instead of a `useCallback` + `useEffect` + `on`/`off` block per event:
 *
 *   bind(SocketEvent.dialogs_created, (data, { rtm }) =>
 *     rtm.create('dialogs', data.brain_id, data.dialog_id)
 *   )
 *
 * `subscribeRtm` owns the parts every event shares: skipping the echo of our own changes,
 * reading the current user and actions at delivery time rather than at subscription time, and
 * keeping one failing handler from affecting the others.
 */

/** Server events whose payload carries the id of the user who caused them. */
export type UserEventName = {
  [Event in keyof ServerToClientEvents]: Parameters<ServerToClientEvents[Event]>[0] extends {
    user_id: string;
  }
    ? Event
    : never;
}[keyof ServerToClientEvents];

export type UserEventData<Event extends UserEventName> =
  ServerToClientEvents[Event] extends (payload: { user_id: string; data: infer Data }) => void
    ? Data
    : never;

export type RtmContext = { rtm: RtmActions; queryClient: QueryClient };

type RtmHandler = (data: unknown, context: RtmContext) => void | Promise<void>;

export type RtmBinding = {
  event: UserEventName;
  handle: RtmHandler;
  /**
   * Apply the event even when the current user caused it. Off by default: our own mutation has
   * already patched the cache, so the echo would only refetch what we just wrote.
   */
  includeOwn?: boolean;
};

/** Builds a binding whose handler is typed by the event's payload. */
export const bind = <Event extends UserEventName>(
  event: Event,
  handle: (data: UserEventData<Event>, context: RtmContext) => void | Promise<void>,
  options: { includeOwn?: boolean } = {}
): RtmBinding => ({ event, handle: handle as RtmHandler, ...options });

type UserEventPayload = { user_id: string; data: unknown };
type RtmListener = (payload: UserEventPayload) => void;

// The typed Socket can't take an event from a union with a listener built for all of them, so
// registration goes through this view of it. Payload types are enforced by `bind` instead.
type ListenerRegistry = {
  on: (event: string, listener: RtmListener) => unknown;
  off: (event: string, listener: RtmListener) => unknown;
};

/**
 * Registers every binding on the socket and returns the function that removes them.
 * `getContext` is read on each delivery, so a listener never acts on a stale user.
 */
export const subscribeRtm = (
  socket: Socket,
  bindings: readonly RtmBinding[],
  getContext: () => RtmContext & { userId: string | undefined }
): (() => void) => {
  const registry = socket as unknown as ListenerRegistry;

  const listeners = bindings.map(({ event, handle, includeOwn = false }) => {
    const listener: RtmListener = ({ user_id, data }) => {
      const { userId, ...context } = getContext();
      // Only an event we can attribute to ourselves is an echo; an unknown user echoes nothing.
      if (!includeOwn && userId !== undefined && user_id === userId) {
        return;
      }
      try {
        Promise.resolve(handle(data, context)).catch((error) => console.error(event, error));
      } catch (error) {
        console.error(event, error);
      }
    };
    registry.on(event, listener);
    return { event, listener };
  });

  return () => {
    listeners.forEach(({ event, listener }) => registry.off(event, listener));
  };
};

/**
 * Subscribes a table of bindings for as long as the calling component is mounted.
 * Pass a module-level (or memoised) table: a new array re-subscribes every binding.
 */
export const useRtmBindings = (socket: Socket | undefined, bindings: readonly RtmBinding[]) => {
  const rtm = useRtm();
  const queryClient = useQueryClient();
  const userId = useSelector(selectUserId);

  const getContext = useEffectEvent(() => ({ rtm, queryClient, userId }));

  useEffect(() => {
    if (!socket) {
      return;
    }
    return subscribeRtm(socket, bindings, () => getContext());
  }, [socket, bindings]);
};
